import { useTranslation } from 'react-i18next';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { LoadingState, ZoneShell } from '@up2d8/ui';
import { AdminLoginPage, PlatformLoginPage } from '../modules/identity/login-page';
import {
  AcceptInvitePage,
  AccountPage,
  ForgotPasswordPage,
  ResetPasswordPage,
} from '../modules/identity/password-pages';
import { AdminHomePage } from '../modules/identity/admin-home-page';
import { InvitationsPage } from '../modules/identity/invitations-screen';
import { NotesPage } from '../modules/example/notes-page';
import { DesignSystemPage } from '../modules/design/design-system-page';
import { TenantsPage } from '../modules/tenants/tenants-page';
import { PeoplePage } from '../modules/directory/people-page';
import { GroupsPage } from '../modules/directory/groups-page';
import { FieldsPage } from '../modules/directory/fields-page';
import { RolesPage } from '../modules/access/roles-page';
import { RoleEditorPage } from '../modules/access/role-editor-page';
import { ImportsPage } from '../modules/import/imports-page';
import { AppLoginPage } from '../modules/app/app-login-page';
import { AppActivatePage } from '../modules/app/app-activate-page';
import { AppHomePage } from '../modules/app/app-home-page';
import { AppPinPage } from '../modules/app/app-pin-page';
import { AppProfilePage } from '../modules/app/app-profile-page';
import { AppPostPage } from '../modules/app/app-post-page';
import { AgendaAdminPage } from '../modules/agenda/agenda-admin-page';
import { ShortcutsAdminPage } from '../modules/shortcuts/shortcuts-admin-page';
import { DocumentsAdminPage } from '../modules/documents/documents-admin-page';
import { ModerationPage } from '../modules/interaction/moderation-page';
import { ReportsPage } from '../modules/reporting/reports-page';
import { SubmissionPage, SubmissionsInboxPage } from '../modules/forms/submissions-inbox';
import { FormsListPage } from '../modules/forms/forms-list-page';
import { FormBuilderPage } from '../modules/forms/form-builder-page';
import { AppFormsPage } from '../modules/app/app-forms-page';
import { AppReportPage } from '../modules/app/app-report-page';
import { ReportsAdminPage } from '../modules/floor-reports/reports-admin-page';
import { SignageAdminPage } from '../modules/signage/signage-admin-page';
import { ScreenDisplay } from '../modules/signage/screen-display';
import { ChecklistsAdminPage } from '../modules/tasks/checklists-admin-page';
import {
  AppHandoverDetailPage,
  AppHandoverLinePage,
  AppHandoverPage,
} from '../modules/app/handover/app-handover-page';
import { AppHandoverWritePage } from '../modules/app/handover/app-handover-write-page';
import { AppJoinPage, AppJoinStartPage } from '../modules/app/join/app-join-page';
import { KioskApp } from '../modules/kiosk/kiosk-app';
import { KiosksAdminPage } from '../modules/kiosk/kiosks-admin-page';
import { HandoverAdminPage } from '../modules/shift-handover/handover-admin-page';
import { AppChecklistRunPage, AppChecklistsPage } from '../modules/app/app-checklists-page';
import { AppTasksPage } from '../modules/app/app-tasks-page';
import { EmergencyPage } from '../modules/emergency/emergency-page';
import { EmergencyOverlay } from '../modules/emergency/emergency-overlay';
import { BrandingPage } from '../modules/tenants/branding-page';
import { AppAgendaPage } from '../modules/app/app-agenda-page';
import { AppDocumentsPage } from '../modules/app/app-documents-page';
import { AppShortcutsPage } from '../modules/app/app-shortcuts-page';
import { AppColleaguesPage } from '../modules/app/app-colleagues-page';
import { SitesPage } from '../modules/tenants/sites-page';
import {
  AppTeamMemberRoutePage,
  AppTeamPage,
  AppTeamPostRoutePage,
  AppTeamPostsRoutePage,
} from '../modules/app/app-team-page';
import { AppTeamTemporaryPage } from '../modules/app/app-team-temporary-page';
import { SchedulesPage } from '../modules/scheduling/schedules-page';
import { AuditPage } from '../modules/audit/audit-page';
import { PlatformAuditPage } from '../modules/audit/platform-audit-page';
import { AppConversationPage } from '../modules/app/app-conversation-page';
import { AppConversationsPage } from '../modules/app/app-conversations-page';
import { CrossTenantConversationPage } from '../modules/app/cross-tenant/cross-tenant-conversation-page';
import { CrossTenantNewPage } from '../modules/app/cross-tenant/cross-tenant-new-page';
import { AppNotificationsPage } from '../modules/app/app-notifications-page';
import { AppSavedPage } from '../modules/app/app-saved-page';
import { AppSettingsPage } from '../modules/app/app-settings-page';
import { AppAccountDeletionPage } from '../modules/app/app-account-deletion-page';
import {
  AccountDeletionCancelPage,
  AccountDeletionPage,
} from '../modules/identity/account-deletion-pages';
import { AppInstallPage } from '../modules/app/app-install-page';
import { NativePush } from '../modules/app/native-push';
import { NativeShell } from '../modules/app/native-shell';
import { AppPrivacyPage } from '../modules/app/app-privacy-page';
import { TenantDetailPage } from '../modules/tenants/tenant-detail-page';
import { ZoneLayout } from './zones/zone-layout';
import type { Session } from '../shared/session';
import { PlatformBrandPage } from '../modules/tenants/platform-brand-page';
import { PlatformStartPage } from '../modules/tenants/platform-start-page';
import { PlatformFeaturesPage } from '../modules/tenants/platform-features-page';
import { OrgTypesPage } from '../modules/tenants/org-types-page';
import { SubscriptionsPage } from '../modules/tenants/subscriptions-page';
import { PlatformAdminsPage, PlatformSystemPage } from '../modules/tenants/platform-system-pages';
import { rootTarget } from '../shared/root-target';

/**
 * De router krijgt geen sessie-object mee maar een functie die de huidige sessie
 * opvraagt. Zo blijft de context stabiel: gaf je het object zelf mee, dan werd de router
 * bij elke wijziging (aanmelden, profiel, laadstatus) opnieuw opgebouwd en verloor het
 * formulier zijn toestand.
 */
export interface RouterContext {
  getSession: () => Session;
}

// De native laag hangt aan de root (MOB-2): een deeplink naar `/app/join/...` komt binnen vóór het
// aanmelden. In een browser doet `NativeShell` niets.
const rootRoute = createRootRoute({
  component: function Root() {
    return (
      <>
        <NativeShell />
        <Outlet />
      </>
    );
  },
});

/**
 * De wortel stuurt naar de app, en een platformbeheerder naar Master (zie `rootTarget`). Zonder
 * sessie stuurt de poortwachter van `/app` door naar `/app/login`.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    const profile = (context as RouterContext).getSession().getProfile();
    throw redirect({ to: rootTarget(profile?.audience) });
  },
});

/**
 * Poortwachter per zone: is er geen sessie, dan eerst stil proberen te vernieuwen met
 * het refresh-cookie. Lukt dat niet, dan naar het aanmeldscherm van díe zone.
 */
async function requireSession(
  getSession: () => Session,
  audience: 'tenant' | 'platform',
  loginPath: '/admin/login' | '/master/login' | '/app/login',
  searchStr = '',
): Promise<void> {
  const session = getSession();
  // getProfile() leest een ref, geen React-state: die is meteen na het aanmelden juist.
  let profile = session.getProfile();
  if (!profile || profile.audience !== audience) {
    // Geen sessie, of die van de andere kant (MST-10): de sessie van deze kant terughalen met haar
    // eigen cookie. Zo wissel je tussen een organisatie en Master zonder opnieuw aan te melden.
    await session.restore(audience);
    profile = getSession().getProfile();
  }
  // Een platformbeheerder hoort niet in de beheerzone van een klant, en omgekeerd.
  if (!profile || profile.audience !== audience) {
    // `?org=` gaat mee naar het aanmeldscherm: de clubapp start op `/app?org=<code>` en moet op het
    // aanmeldscherm van die club uitkomen (login-mode.ts), niet op de standaard.
    const org = new URLSearchParams(searchStr).get('org');
    if (org) throw redirect({ href: `${loginPath}?org=${encodeURIComponent(org)}` });
    throw redirect({ to: loginPath });
  }
}

// --- Publieke schermen ---------------------------------------------------------------
// Alles wat je zonder sessie moet kunnen bereiken: aanmelden, een uitnodiging aanvaarden
// en een vergeten wachtwoord herstellen.

/**
 * De weergave op een digitaal scherm (Stap 41).
 *
 * Buiten elke zone: geen sessie, geen poortwachter en geen navigatie. Het scherm koppelt zich met
 * een code en houdt daarna een token in zijn browser. Dit is de enige route in de applicatie die
 * voor niemand aanmelding vraagt — en achter dat token staat precies wat er op dat scherm staat:
 * geen namen, geen persoonsgegevens (ADR-0063).
 */
// De kiosk (Stap 40, KIO-2): volledig scherm, buiten de app-schil en buiten de sessie van de app.
const kioskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kiosk',
  component: KioskApp,
});

const signageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signage',
  component: ScreenDisplay,
});

const adminLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/login',
  component: AdminLoginPage,
});

const platformLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/master/login',
  component: PlatformLoginPage,
});

/**
 * De platformzone heette `/platform` tot MST-1; sindsdien is het Master op `/master`. Oude
 * bladwijzers en links in mails sturen door, met de rest van het pad erbij. `tenants` werd
 * `organisaties`.
 */
const legacyPlatformRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform',
  beforeLoad: () => {
    throw redirect({ href: '/master' });
  },
});

const legacyPlatformSplatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform/$',
  beforeLoad: ({ params }) => {
    const rest = (params as { _splat?: string })._splat ?? '';
    throw redirect({ href: `/master/${rest.replace(/^tenants(?=\/|$)/, 'organisaties')}` });
  },
});

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/accept-invite',
  validateSearch: (search: Record<string, unknown>) => ({
    token: String(search.token ?? ''),
    ...(search.to === 'app' ? { to: 'app' as const } : {}),
  }),
  component: AcceptInvitePage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/forgot-password',
  component: ForgotPasswordPage,
});

// Account verwijderen zonder aanmelden (DEL-1): de winkels eisen een webpagina, ook voor wie de
// app al weg deed. Publiek zoals wachtwoord vergeten; de link in de mail komt hier terug.
const accountDeletionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account-verwijderen',
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.token === 'string' ? { token: search.token } : {}),
  }),
  component: AccountDeletionPage,
});

const accountDeletionCancelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account-verwijderen/annuleren',
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.token === 'string' ? { token: search.token } : {}),
  }),
  component: AccountDeletionCancelPage,
});

// --- Medewerkerszone -----------------------------------------------------------------
// De `app`-zone draait op de AA-tokenset: grotere tekst, grotere raakvlakken (ADR-0018).

const appLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/login',
  component: AppLoginPage,
});

// Zelfregistratie met een uitnodiging (Stap 34, REG-2): de code intypen, of de link uit de QR.
const appJoinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/join',
  component: function AppJoin() {
    return <AppJoinPage />;
  },
});

const startRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/start/$code',
  component: AppJoinStartPage,
});

const appActivateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/activate',
  component: AppActivatePage,
});

/**
 * De wachttekst van een route die nog laadt.
 *
 * Een eigen component omdat `t()` een hook nodig heeft en een `fallback` geen component is maar
 * een element. Stond er "Laden…" als vaste tekst (Extra 2, punt 12).
 */
function SuspenseLaden() {
  const { t } = useTranslation();
  return <LoadingState rows={6} label={t('common.loading')} />;
}

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) =>
    requireSession(
      (context as RouterContext).getSession,
      'tenant',
      '/app/login',
      location.searchStr,
    ),
  // De noodmelding hangt hier en niet op het beginscherm (Stap 35): een ontruiming mag niemand
  // missen omdat hij net in een gesprek zat te lezen. Loopt er niets, dan rendert `EmergencyOverlay`
  // niets en kost dit één verzoek per kwartier.
  component: function AppZone() {
    return (
      <>
        <EmergencyOverlay />
        <NativePush />
        <Outlet />
      </>
    );
  },
});

const appHomeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app',
  component: AppHomePage,
});

const appPinRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/settings/pin',
  component: AppPinPage,
});

const appAccountDeletionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/settings/account-verwijderen',
  component: AppAccountDeletionPage,
});

const appProfileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/profile',
  component: AppProfilePage,
});

const appPostRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/posts/$postId',
  component: AppPostPage,
});

const appAgendaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/agenda',
  component: AppAgendaPage,
});

const appDocumentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/documenten',
  component: AppDocumentsPage,
});

const appFormsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/formulieren',
  // `?formulier=` opent meteen dat formulier: zo linkt "Mijn taken" ernaar (VLR-3).
  validateSearch: (search: Record<string, unknown>): { formulier?: string } =>
    typeof search.formulier === 'string' ? { formulier: search.formulier } : {},
  component: AppFormsPage,
});

const appReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/melden',
  component: AppReportPage,
});

const appChecklistsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/checklists',
  component: AppChecklistsPage,
});

// Eén uitvoering (VLR-2): het adres waar "Mijn taken" al naartoe linkte, en waar de terugknop van
// de telefoon vanaf de lijst uit kan.
const appChecklistRunRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/checklists/$runId',
  component: AppChecklistRunPage,
});

/**
 * "Mijn taken": alles wat er voor deze medewerker open staat (SCH-6).
 *
 * De checklistpagina blijft bestaan - een checklist in deze lijst linkt erheen - maar ze staat niet
 * meer in de zijbalk. Twee items die allebei op een takenlijst lijken, laten de lezer kiezen zonder
 * dat hij het verschil kan weten.
 */
const appTasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/taken',
  component: AppTasksPage,
});

/** Alle tegels; het beginscherm toont er hoogstens acht (UI-2). */
const appShortcutsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/shortcuts',
  component: AppShortcutsPage,
});

const appColleaguesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/collegas',
  // Uit het globale zoeken: de collega die je aantikte (`persoon`) en zijn naam als zoekterm.
  validateSearch: (search: Record<string, unknown>): { persoon?: string; zoek?: string } => ({
    ...(typeof search.persoon === 'string' ? { persoon: search.persoon } : {}),
    ...(typeof search.zoek === 'string' ? { zoek: search.zoek } : {}),
  }),
  component: AppColleaguesPage,
});

/*
  Mijn team (Stap 17, TEAM-2).

  Drie routes en geen toestand in één scherm, om twee redenen. De escalatiemelding moet op het
  bericht landen (frame 4 -> 2a), en dat vraagt een adres. En een ploegbaas die een bericht
  openhoudt en de pagina ververnieuwt, hoort op datzelfde bericht terug te komen.
*/
// De ploegoverdracht (Stap 39, OVD-2): lezen per lijn, één overdracht, en schrijven.
const appHandoverRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/overdracht',
  component: function AppHandover() {
    return <AppHandoverPage />;
  },
});

const appHandoverLineRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/overdracht/lijn/$lineId',
  component: AppHandoverLinePage,
});

const appHandoverDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/overdracht/$handoverId',
  component: AppHandoverDetailPage,
});

const appHandoverWriteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/overdracht/schrijven/$lineId/$slotId',
  component: AppHandoverWritePage,
});

const appTeamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/team',
  component: AppTeamPage,
});

const appTeamPostsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/team/berichten',
  component: AppTeamPostsRoutePage,
});

const appTeamPostRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/team/berichten/$postId',
  component: AppTeamPostRoutePage,
});

// Een uitzendkracht toevoegen (Stap 31).
const appTeamTemporaryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/team/tijdelijk',
  component: AppTeamTemporaryPage,
});

const appTeamMemberRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/team/leden/$personId',
  component: AppTeamMemberRoutePage,
});

const appConversationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/gesprekken',
  component: AppConversationsPage,
});

const appConversationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/gesprekken/$conversationId',
  component: AppConversationPage,
});

// Gesprekken over bedrijven heen (ACC-4): een eigen pad, want het id hoort bij een ander soort
// gesprek. `nieuw` is een vaste tak en wint van het id.
const appCrossTenantNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/over-bedrijven/nieuw',
  component: CrossTenantNewPage,
});

const appCrossTenantConversationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/over-bedrijven/$conversationId',
  component: CrossTenantConversationPage,
});

const appSavedRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/opgeslagen',
  component: AppSavedPage,
});

const appNotificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/notifications',
  component: AppNotificationsPage,
});

const appSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/settings',
  component: AppSettingsPage,
});

// De voorkeuren staan op het instellingenscherm; dit pad komt uit de stap (Stap 16).
const appSettingsNotificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/settings/notifications',
  component: AppSettingsPage,
});

const appPrivacyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/privacy/werkgever',
  component: AppPrivacyPage,
});

const appInstallRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/app/install',
  component: AppInstallPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/reset-password',
  validateSearch: (search: Record<string, unknown>) => ({ token: String(search.token ?? '') }),
  component: ResetPasswordPage,
});

// --- Beheerzone ----------------------------------------------------------------------

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'admin',
  beforeLoad: ({ context }) =>
    requireSession((context as RouterContext).getSession, 'tenant', '/admin/login'),
  component: Outlet,
});

/**
 * Uitnodigingen (Extra 4).
 *
 * Het pad komt uit Stap 34: die stap voegt het tabblad "Medewerkers" toe aan hetzelfde scherm.
 */
const adminInvitationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/invitations',
  component: function AdminInvitations() {
    return (
      <ZoneLayout zone="admin" titleKey="invitations.title" activeKey="invitations" ownHeading>
        <InvitationsPage />
      </ZoneLayout>
    );
  },
});

const adminDashboardRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin',
  component: function AdminDashboard() {
    return (
      <ZoneLayout zone="admin" titleKey="beheer.start.title" activeKey="admin-dashboard" ownHeading>
        {/* De demopagina uit Stap 0 staat nog op /platform; daar is ze nog wat waard. */}
        {/* De demokaart (ACC-3) staat sinds BHR-2 in de pagina zelf, onder de paginakop. */}
        <AdminHomePage />
      </ZoneLayout>
    );
  },
});

const adminAccountRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/account',
  component: function AdminAccount() {
    return (
      <ZoneLayout
        zone="admin"
        titleKey="beheer.shell.changePassword"
        activeKey="account"
        ownHeading
      >
        <AccountPage />
      </ZoneLayout>
    );
  },
});

const adminPeopleRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/people',
  // Uit het globale zoeken: het paneel van de medewerker die je aantikte.
  validateSearch: (search: Record<string, unknown>): { persoon?: string } =>
    typeof search.persoon === 'string' ? { persoon: search.persoon } : {},
  component: function AdminPeople() {
    return (
      <ZoneLayout zone="admin" titleKey="people.title" activeKey="people" ownHeading>
        <PeoplePage />
      </ZoneLayout>
    );
  },
});

/*
  Vestigingen stond hier zonder schil (sinds Stap 29): geen zijbalk, geen kopbalk. Bij BHR-0
  rechtgezet, want de nieuwe zijbalk heeft een plek voor het item en die moet dan ook oplichten.
  `ownHeading`: de pagina heeft al een eigen `h1`.
*/
const adminSitesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/vestigingen',
  component: function AdminSites() {
    return (
      <ZoneLayout zone="admin" titleKey="sites.title" activeKey="sites" ownHeading>
        <SitesPage />
      </ZoneLayout>
    );
  },
});

const adminGroupsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/groups',
  component: function AdminGroups() {
    return (
      <ZoneLayout zone="admin" titleKey="groups.title" activeKey="groups" ownHeading>
        <GroupsPage />
      </ZoneLayout>
    );
  },
});

const adminFieldsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/fields',
  component: function AdminFields() {
    return (
      <ZoneLayout zone="admin" titleKey="fields.title" activeKey="fields" ownHeading>
        <FieldsPage />
      </ZoneLayout>
    );
  },
});

const adminRolesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/roles',
  component: function AdminRoles() {
    return (
      <ZoneLayout zone="admin" titleKey="roles.title" activeKey="roles" ownHeading>
        <RolesPage />
      </ZoneLayout>
    );
  },
});

/**
 * Een rol toevoegen of bewerken op een eigen pagina (BHR-3, O7): 77 rechten passen niet in een
 * venster. `new` is een vast pad en wint van `$roleId`.
 */
const adminRoleNewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/roles/new',
  component: function AdminRoleNew() {
    return (
      <ZoneLayout zone="admin" titleKey="roles.title" activeKey="roles" ownHeading>
        <RoleEditorPage roleId={null} />
      </ZoneLayout>
    );
  },
});

const adminRoleRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/roles/$roleId',
  component: function AdminRole() {
    const { roleId } = adminRoleRoute.useParams();
    return (
      <ZoneLayout zone="admin" titleKey="roles.title" activeKey="roles" ownHeading>
        <RoleEditorPage roleId={roleId} />
      </ZoneLayout>
    );
  },
});

const adminImportsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/imports',
  component: function AdminImports() {
    return (
      <ZoneLayout zone="admin" titleKey="imports.title" activeKey="imports" ownHeading>
        <ImportsPage />
      </ZoneLayout>
    );
  },
});

/**
 * Het opstelscherm komt apart binnen.
 *
 * De tekstschrijver (TipTap) is het grootste stuk JavaScript van de hele applicatie, en hij
 * hoort bij één scherm waar een handvol mensen komt. Zonder deze splitsing zou elke
 * medewerker hem meedownloaden op zijn telefoon, om een feed te lezen.
 */
const PostsPage = lazy(async () => ({
  default: (await import('../modules/communication/posts-page')).PostsPage,
}));

const adminPostsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/posts',
  // Het bereikrapport staat in de URL, en daar is een reden voor: dat rapport is het antwoord op
  // "heeft iedereen het gelezen", en dat antwoord wordt doorgestuurd. Een scherm dat na F5 of na
  // een doorgestuurde link terugvalt op de berichtenlijst, kost iemand elke keer vier kliks.
  validateSearch: (search: Record<string, unknown>) => ({
    bereik: search.bereik ? String(search.bereik) : undefined,
    // Een melding "ingediend bericht" opent meteen de editor van dat bericht (Stap 15).
    bericht: search.bericht ? String(search.bericht) : undefined,
  }),
  component: function AdminPosts() {
    return (
      <ZoneLayout zone="admin" titleKey="posts.title" activeKey="posts" ownHeading>
        <Suspense fallback={<SuspenseLaden />}>
          <PostsPage />
        </Suspense>
      </ZoneLayout>
    );
  },
});

const adminBrandingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/huisstijl',
  component: function AdminBranding() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.branding" activeKey="branding" ownHeading>
        <BrandingPage />
      </ZoneLayout>
    );
  },
});

const adminReportsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/reports',
  component: function AdminReports() {
    return (
      <ZoneLayout zone="admin" titleKey="reports.title" activeKey="reports" ownHeading>
        <ReportsPage />
      </ZoneLayout>
    );
  },
});

const adminChecklistsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/checklists',
  component: function AdminChecklists() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.checklists" activeKey="checklists" ownHeading>
        <ChecklistsAdminPage />
      </ZoneLayout>
    );
  },
});

const adminHandoverRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/overdracht',
  component: function AdminHandover() {
    return (
      <ZoneLayout zone="admin" titleKey="handoverAdmin.title" activeKey="shift-handover" ownHeading>
        <HandoverAdminPage />
      </ZoneLayout>
    );
  },
});

const adminKiosksRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/kiosks',
  component: function AdminKiosks() {
    return (
      <ZoneLayout zone="admin" titleKey="kiosksAdmin.title" activeKey="kiosks" ownHeading>
        <KiosksAdminPage />
      </ZoneLayout>
    );
  },
});

const adminSignageRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/schermen',
  component: function AdminSignage() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.signage" activeKey="signage" ownHeading>
        <SignageAdminPage />
      </ZoneLayout>
    );
  },
});

const adminFloorReportsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/meldingen',
  component: function AdminFloorReports() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.alerts" activeKey="floor-reports" ownHeading>
        <ReportsAdminPage />
      </ZoneLayout>
    );
  },
});

const adminFormsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/formulieren',
  component: function AdminForms() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.forms" activeKey="forms" ownHeading>
        <FormsListPage />
      </ZoneLayout>
    );
  },
});

/** De formulierbouwer op een eigen adres (BHR-5, frame C6), zodat een formulier te delen is. */
const adminFormBuilderRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/formulieren/$formId',
  component: function AdminFormBuilder() {
    const { formId } = adminFormBuilderRoute.useParams();
    return (
      <ZoneLayout zone="admin" titleKey="nav.forms" activeKey="forms" ownHeading>
        <FormBuilderPage formId={formId} />
      </ZoneLayout>
    );
  },
});

/**
 * De behandelaarsinbox staat apart van de bouwer, en dat volgt de rechten: een teamleider met
 * `forms.submission.handle` hoort hier te kunnen werken zonder de bouwer te zien.
 */
const adminSubmissionsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/aanvragen',
  component: function AdminSubmissions() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.requests" activeKey="submissions" ownHeading>
        <SubmissionsInboxPage />
      </ZoneLayout>
    );
  },
});

/**
 * Eén aanvraag op haar eigen adres (Extra 6, punt 8).
 *
 * Zodat een melding over een nieuwe aanvraag ergens naartoe kan springen, en een behandelaar een
 * link kan doorsturen.
 */
const adminSubmissionRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/aanvragen/$submissionId',
  component: function AdminSubmission() {
    const { submissionId } = adminSubmissionRoute.useParams();
    return (
      <ZoneLayout zone="admin" titleKey="nav.requests" activeKey="submissions" ownHeading>
        <SubmissionPage submissionId={submissionId} />
      </ZoneLayout>
    );
  },
});

const adminEmergencyRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/noodkanaal',
  component: function AdminEmergency() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.emergency" activeKey="emergency" ownHeading>
        <EmergencyPage />
      </ZoneLayout>
    );
  },
});

const adminModerationRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/moderatie',
  component: function AdminModeration() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.moderation" activeKey="moderation" ownHeading>
        <ModerationPage />
      </ZoneLayout>
    );
  },
});

const adminDocumentsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/documenten',
  component: function AdminDocuments() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.documents" activeKey="documents" ownHeading>
        <DocumentsAdminPage />
      </ZoneLayout>
    );
  },
});

const adminShortcutsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/shortcuts',
  component: function AdminShortcuts() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.shortcuts" activeKey="shortcuts" ownHeading>
        <ShortcutsAdminPage />
      </ZoneLayout>
    );
  },
});

const adminAgendaRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/agenda',
  component: function AdminAgenda() {
    return (
      <ZoneLayout zone="admin" titleKey="agendaAdmin.title" activeKey="agenda" ownHeading>
        <AgendaAdminPage />
      </ZoneLayout>
    );
  },
});

// Het auditspoor (Stap 18).
const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/audit',
  component: function AdminAudit() {
    return (
      <ZoneLayout zone="admin" titleKey="audit.title" activeKey="audit" ownHeading>
        <AuditPage />
      </ZoneLayout>
    );
  },
});

// Alles wat gepland staat (Stap 14).
const adminSchedulesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/schedules',
  component: function AdminSchedules() {
    return (
      <ZoneLayout zone="admin" titleKey="scheduling.title" activeKey="schedules" ownHeading>
        <SchedulesPage />
      </ZoneLayout>
    );
  },
});

const adminExampleRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/example',
  component: function AdminExample() {
    return (
      <ZoneLayout zone="admin" titleKey="nav.exampleNotes" activeKey="example">
        <NotesPage />
      </ZoneLayout>
    );
  },
});

// --- Platformzone --------------------------------------------------------------------

const platformRoute = createRoute({
  getParentRoute: () => rootRoute,
  // Niet 'platform': dan botst de id met de doorverwijzing op `/platform` (MST-1) en start de router niet.
  id: 'master',
  beforeLoad: ({ context }) =>
    requireSession((context as RouterContext).getSession, 'platform', '/master/login'),
  component: Outlet,
});

const platformDashboardRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master',
  component: function PlatformDashboard() {
    return (
      <ZoneLayout
        zone="platform"
        titleKey="dashboard.platformTitle"
        activeKey="platform-dashboard"
        ownHeading
      >
        <PlatformStartPage />
      </ZoneLayout>
    );
  },
});

/**
 * Het designsysteem (Extra 1).
 *
 * Verhuisd van `/admin/design` naar de platformzone: het is een pagina voor ontwikkelaars, met
 * elke component en elk token erop. In het beheerportaal stond ze in het menu van de klant - en
 * een klant die een pagina "Designsysteem" opent, krijgt een catalogus te zien in plaats van zijn
 * eigen bedrijf. Hier staat ze bij de mensen voor wie ze bedoeld is.
 */
const platformDesignRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master/design',
  component: function PlatformDesign() {
    return (
      <ZoneLayout zone="platform" titleKey="nav.designSystem" activeKey="design" ownHeading>
        <DesignSystemPage />
      </ZoneLayout>
    );
  },
});

/**
 * Het merk van het product (MRK-3, ADR-0084).
 *
 * Achter `aud=platform` zoals elke route hier; het recht `tenants.brand.manage` controleert de API,
 * en het scherm zegt het zelf als het ontbreekt (een platformbeheerder heeft het standaard).
 */
const platformBrandRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master/merk',
  component: function PlatformBrand() {
    return (
      <ZoneLayout zone="platform" titleKey="beheer.merk.nav" activeKey="platform-brand" ownHeading>
        <PlatformBrandPage />
      </ZoneLayout>
    );
  },
});

/**
 * Het auditspoor van het platform (AUD-1, ADR-0088): wat platformbeheerders deden, en wat geen
 * bedrijf heeft (het merk). Het recht `platform.audit.view` controleert de API; het scherm zegt het
 * zelf als het ontbreekt.
 */
const platformAuditRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master/audit',
  component: function PlatformAudit() {
    return (
      <ZoneLayout
        zone="platform"
        titleKey="beheer.platformAudit.title"
        activeKey="platform-audit"
        ownHeading
      >
        <PlatformAuditPage />
      </ZoneLayout>
    );
  },
});

/** Functies over alle organisaties heen (MST-4): de platformstandaard en de matrix. */
const platformFeaturesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master/functies',
  component: function PlatformFeatures() {
    return (
      <ZoneLayout
        zone="platform"
        titleKey="master.featuresPage.title"
        activeKey="platform-features"
        ownHeading
      >
        <PlatformFeaturesPage />
      </ZoneLayout>
    );
  },
});

/**
 * Organisatietypes (ORG-3, ADR-0099): per type de functies, de aanmeldwijze en de rolnamen waarmee
 * een organisatie start. Het recht `tenants.tenant.manage` controleert de API.
 */
const platformOrgTypesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master/organisatietypes',
  component: function PlatformOrgTypes() {
    return (
      <ZoneLayout
        zone="platform"
        titleKey="beheer.orgType.page.title"
        activeKey="platform-org-types"
        ownHeading
      >
        <OrgTypesPage />
      </ZoneLayout>
    );
  },
});

/** Abonnementen (MST-6): omzet, upsell-analyse en pakketten. */
const platformSubscriptionsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master/abonnementen',
  component: function PlatformSubscriptions() {
    return (
      <ZoneLayout
        zone="platform"
        titleKey="master.billing.title"
        activeKey="platform-subscriptions"
        ownHeading
      >
        <SubscriptionsPage />
      </ZoneLayout>
    );
  },
});

/** Wie Master mag gebruiken (MST-7). */
const platformAdminsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master/beheerders',
  component: function PlatformAdmins() {
    return (
      <ZoneLayout
        zone="platform"
        titleKey="master.admins.title"
        activeKey="platform-admins"
        ownHeading
      >
        <PlatformAdminsPage />
      </ZoneLayout>
    );
  },
});

/** De systeemstatus (MST-8). */
const platformSystemRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master/systeem',
  component: function PlatformSystem() {
    return (
      <ZoneLayout
        zone="platform"
        titleKey="master.system.title"
        activeKey="platform-system"
        ownHeading
      >
        <PlatformSystemPage />
      </ZoneLayout>
    );
  },
});

const platformTenantsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master/organisaties',
  component: function PlatformTenants() {
    return (
      <ZoneLayout zone="platform" titleKey="tenants.title" activeKey="platform-tenants" ownHeading>
        <TenantsPage />
      </ZoneLayout>
    );
  },
});

const platformTenantDetailRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/master/organisaties/$tenantId',
  component: function PlatformTenantDetail() {
    return (
      <ZoneLayout zone="platform" titleKey="nav.company" activeKey="platform-tenants" ownHeading>
        {/* Terugzetten van een demobedrijf staat sinds BHR-8 als tab op de pagina zelf. */}
        <TenantDetailPage />
      </ZoneLayout>
    );
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  signageRoute,
  kioskRoute,
  adminLoginRoute,
  platformLoginRoute,
  legacyPlatformRoute,
  legacyPlatformSplatRoute,
  acceptInviteRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  accountDeletionRoute,
  accountDeletionCancelRoute,
  appLoginRoute,
  appActivateRoute,
  appJoinRoute,
  startRoute,
  appRoute.addChildren([
    appHomeRoute,
    appPinRoute,
    appAccountDeletionRoute,
    appProfileRoute,
    appPostRoute,
    appAgendaRoute,
    appDocumentsRoute,
    appFormsRoute,
    appReportRoute,
    appChecklistsRoute,
    appChecklistRunRoute,
    appTasksRoute,
    appShortcutsRoute,
    appColleaguesRoute,
    appTeamRoute,
    appHandoverRoute,
    appHandoverLineRoute,
    appHandoverDetailRoute,
    appHandoverWriteRoute,
    appTeamPostsRoute,
    appTeamPostRoute,
    appTeamMemberRoute,
    appTeamTemporaryRoute,
    appConversationsRoute,
    appConversationRoute,
    appCrossTenantNewRoute,
    appCrossTenantConversationRoute,
    appSavedRoute,
    appNotificationsRoute,
    appSettingsRoute,
    appSettingsNotificationsRoute,
    appPrivacyRoute,
    appInstallRoute,
  ]),
  adminRoute.addChildren([
    adminDashboardRoute,
    adminInvitationsRoute,
    adminPeopleRoute,
    adminGroupsRoute,
    adminSitesRoute,
    adminFieldsRoute,
    adminRolesRoute,
    adminRoleNewRoute,
    adminRoleRoute,
    adminImportsRoute,
    adminPostsRoute,
    adminAccountRoute,
    adminAgendaRoute,
    adminSchedulesRoute,
    adminAuditRoute,
    adminDocumentsRoute,
    adminReportsRoute,
    adminEmergencyRoute,
    adminFormsRoute,
    adminFormBuilderRoute,
    adminSubmissionsRoute,
    adminSubmissionRoute,
    adminFloorReportsRoute,
    adminSignageRoute,
    adminChecklistsRoute,
    adminHandoverRoute,
    adminKiosksRoute,
    adminModerationRoute,
    adminShortcutsRoute,
    adminBrandingRoute,
    adminExampleRoute,
  ]),
  platformRoute.addChildren([
    platformDashboardRoute,
    platformFeaturesRoute,
    platformOrgTypesRoute,
    platformSubscriptionsRoute,
    platformAdminsRoute,
    platformSystemRoute,
    platformDesignRoute,
    platformBrandRoute,
    platformAuditRoute,
    platformTenantsRoute,
    platformTenantDetailRoute,
  ]),
]);

export function buildRouter(getSession: () => Session) {
  return createRouter({
    routeTree,
    context: { getSession } satisfies RouterContext,
    defaultPendingComponent: () => (
      <ZoneShell zone="admin">
        <LoadingState rows={3} />
      </ZoneShell>
    ),
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
